import { ChannelPartnerFormType } from 'src/components/channel-partners/channelPartners';
import { ActivityLog } from 'src/interfaces/common/IdValue';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { Availability } from 'src/interfaces/technician/technician';
import axiosClient from 'src/utils/axios-client';
import { passwordGenerator } from 'src/utils/generate-password';
import { v4 as uuidv4 } from 'uuid';
import { amplifyConfig, APIConfig, throttle } from '../config';
import { wait } from '../utils/wait';

class ChannelPartnerApi {
  private channelPartnerApiUrl = APIConfig.channelPartnerApiUrl;

  private requestManagementUrl = APIConfig.requestManagementUrl;

  async sendInviteChannelPartner(attributes: any):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const authChallenge = uuidv4();

      const signUpAttributes = { ...attributes,
        password: passwordGenerator(12),
        username: attributes.email,
        group: 'Channel-Partner',
        authChallenge,
        appClientId: amplifyConfig.aws_user_pools_web_client_id
      };
      const res = await axiosClient
        .post(`${this.channelPartnerApiUrl}channel-partner/sendInvite`, signUpAttributes)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async addChannelPartnerOnCognito(attributes: any):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const authChallenge = uuidv4();

      const signUpAttributes = { ...attributes,
        password: passwordGenerator(12),
        username: attributes.email,
        group: 'Channel-Partner',
        authChallenge,
        appClientId: amplifyConfig.aws_user_pools_web_client_id
      };
      const res = await axiosClient
        .post(`${this.channelPartnerApiUrl}channel-partner/addToCognito`, signUpAttributes)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async verifyChannelPartner(authChallengeResponse: string):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const authChallengeResponses = authChallengeResponse.split('&');

      const attributes = {
        username: authChallengeResponses[0],
        authChallenge: authChallengeResponses[1],
      };
      console.log(attributes);

      const res = await axiosClient
        .post(`${this.channelPartnerApiUrl}channel-partner/verify`, attributes)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async onboardNewChannelPartner(attributes: any):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const body = {
        ...attributes,
      };

      const res = await axiosClient
        .post(`${this.channelPartnerApiUrl}channel-partner`, body)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getChannelPartnerById(channelPartnerId: string):
  Promise<{ responseData: ChannelPartnerFormType | any, statusCode: number }> {
    try {
      const res = await axiosClient
        .get(`${this.channelPartnerApiUrl}channel-partner/getById/${channelPartnerId}`)
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async updateChannelPartner(attributes: any):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const body = {
        ...attributes,
      };

      const res = await axiosClient
        .put(`${this.channelPartnerApiUrl}channel-partner`, body)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async updateChannelPartnerStatus(attributes: any):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const body = {
        ...attributes,
      };

      const res = await axiosClient
        .put(`${this.channelPartnerApiUrl}channel-partner/status`, body)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getChannelPartnersByPaginationAndFilters(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    if (throttle) {
      await wait(throttle);
    }
    try {
      const res = await axiosClient
        .post(`${this.channelPartnerApiUrl}channel-partner/getByPaginationAndFilter`, options)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getVerifiedCpFilterData():
  Promise<{ responseData: any, statusCode: number }> {
    if (throttle) {
      await wait(throttle);
    }
    try {
      const res = await axiosClient
        .get(`${this.channelPartnerApiUrl}channel-partner/filter/data`)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getActivityLogsById(id: string):
  Promise<{ responseData: ActivityLog[] | any, statusCode: number }> {
    try {
      const res = await axiosClient
        .get(`${this.channelPartnerApiUrl}activityLogs/${id}`)
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async checkCPExistByEmail(primaryEmail: string):
  Promise<{ responseData: any | any, statusCode: number }> {
    try {
      const body = {
        primaryEmail
      };
      const res = await axiosClient
        .post(`${this.channelPartnerApiUrl}channel-partner/emailExist/`, body)
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getActiveChannelPartners():
  Promise<{ responseData: any, statusCode: number }> {
    if (throttle) {
      await wait(throttle);
    }
    try {
      const res = await axiosClient.get(`${this.channelPartnerApiUrl}channel-partner/list/active`)
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async revokeChannelPartnerInvite(username: string, id: string):
  Promise<{ responseData: any | any, statusCode: number }> {
    try {
      const body = {
        username,
        id
      };
      const res = await axiosClient
        .post(`${this.channelPartnerApiUrl}channel-partner/revokeInvite/`, body)
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async resendChannelPartnerInvite(attributes: any):
  Promise<{ responseData: any | any, statusCode: number }> {
    try {
      const authChallenge = uuidv4();
      const resendAttributes = { ...attributes,
        password: passwordGenerator(12),
        username: attributes.email,
        group: 'Channel-Partner',
        authChallenge,
        appClientId: amplifyConfig.aws_user_pools_web_client_id
      };
      const res = await axiosClient
        .post(`${this.channelPartnerApiUrl}channel-partner/resendInvite/`, resendAttributes)
        .then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  // calls GET api to get channel Partner availability slots present in the database
  async getChannelPartnerAvailabilitySlots(channelPartnerId: string, dates: any):
  Promise<{ responseData: Availability[], statusCode: number }> {
    try {
      const res = await axiosClient.get(`${this.requestManagementUrl}requests/calendar/timeSlots/channelPartner/${channelPartnerId}`, { params: dates }).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response?.data || error.message || 'Network error',
        statusCode: error.response?.status || 500
      });
    }
  }

  async addServiceAreas(serviceAreas: string[], cpId: string):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient
        .patch(`${this.channelPartnerApiUrl}channel-partner/${cpId}/serviceAreas`, serviceAreas)
        .then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response?.data || error.message || 'Network error',
        statusCode: error.response?.status || 500
      });
    }
  }
}

export const channelPartnerApi = new ChannelPartnerApi();
